import { StoreProvider, useRegistry } from './app/store.js';
import { RouterProvider, useRouter } from './app/router.js';
import { Shell } from './app/Shell.js';
import { LockGate } from './app/LockGate.js';
import { AuthGate } from './app/AuthGate.js';
import { TodayScreen } from './routes/Today.js';
import { CaptureScreen } from './routes/Capture.js';
import { TasksScreen } from './routes/Tasks.js';
import { SearchScreen } from './routes/Search.js';
import { AllSystemsScreen } from './routes/AllSystems.js';
import { SourceReviewScreen } from './routes/SourceReview.js';
import { RuntimeScreen } from './routes/Runtime.js';
import { CalendarScreen } from './routes/Calendar.js';
import { ReviewsScreen } from './routes/Reviews.js';
import { TargetsScreen } from './routes/Targets.js';
import { RoutinesScreen } from './routes/Routines.js';
import { ProblemsScreen } from './routes/Problems.js';
import { ProjectsScreen } from './routes/Projects.js';
import { FinanceScreen } from './routes/Finance.js';
import { DirectionScreen } from './routes/Direction.js';
import { DirectionRolesScreen } from './routes/DirectionRoles.js';
import { DirectionValuesScreen } from './routes/DirectionValues.js';
import { DirectionChallengesScreen } from './routes/DirectionChallenges.js';
import { HealthScreen } from './routes/Health.js';
import { AssistantScreen } from './routes/Assistant.js';
import { StudyScreen } from './routes/Study.js';
import { AcademicsScreen } from './routes/Academics.js';
import { CareerScreen } from './routes/Career.js';
import { MentalScreen } from './routes/Mental.js';
import { SpiritualScreen } from './routes/Spiritual.js';
import { WorkScreen } from './routes/Work.js';
import { BusinessScreen } from './routes/Business.js';
import { EnvironmentScreen } from './routes/Environment.js';
import { FocusScreen } from './routes/Focus.js';
import { RemindersScreen } from './routes/Reminders.js';
import { SecurityScreen } from './routes/Security.js';
import { NotesScreen } from './routes/Notes.js';
import { KnowledgeGraphScreen } from './routes/KnowledgeGraph.js';
import { DomainHub } from './routes/DomainHub.js';
import type { JSX } from 'react';

/** Bespoke, fully-built screens. Every other registered route uses DomainHub. */
const BESPOKE: Record<string, () => JSX.Element> = {
  '/today': TodayScreen,
  '/capture': CaptureScreen,
  '/tasks': TasksScreen,
  '/search': SearchScreen,
  '/all-systems': AllSystemsScreen,
  '/source-review': SourceReviewScreen,
  '/runtime': RuntimeScreen,
  '/calendar': CalendarScreen,
  '/reviews': ReviewsScreen,
  '/targets': TargetsScreen,
  '/routines': RoutinesScreen,
  '/problems': ProblemsScreen,
  '/projects': ProjectsScreen,
  '/finance': FinanceScreen,
  '/direction': DirectionScreen,
  '/direction/roles': DirectionRolesScreen,
  '/direction/values': DirectionValuesScreen,
  '/direction/challenges': DirectionChallengesScreen,
  '/health': HealthScreen,
  '/assistant': AssistantScreen,
  '/study': StudyScreen,
  '/academics': AcademicsScreen,
  '/career': CareerScreen,
  '/mental': MentalScreen,
  '/spiritual': SpiritualScreen,
  '/work': WorkScreen,
  '/business': BusinessScreen,
  '/environment': EnvironmentScreen,
  '/focus': FocusScreen,
  '/reminders': RemindersScreen,
  '/security': SecurityScreen,
  '/knowledge/notes': NotesScreen,
  '/knowledge/graph': KnowledgeGraphScreen,
};

function CurrentScreen() {
  const { path } = useRouter();
  const registry = useRegistry();

  const Bespoke = BESPOKE[path];
  if (Bespoke) return <Bespoke />;

  // Any registered system route gets the honest, discoverable Domain Hub.
  const known = registry
    .all()
    .some((s) => s.surface.kind === 'route' && s.surface.path === path);
  if (known) return <DomainHub />;

  // Unknown path -> Today.
  return <TodayScreen />;
}

export function App() {
  return (
    <StoreProvider>
      <RouterProvider>
        <AuthGate>
          <LockGate>
            <Shell>
              <CurrentScreen />
            </Shell>
          </LockGate>
        </AuthGate>
      </RouterProvider>
    </StoreProvider>
  );
}
